import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerRoleProfileSkillMutationResponse, SkillTopicItemDto } from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';
import { TopicDependencyOption } from './add-profile-topic-dialog.component';

export interface EditProfileTopicDialogData {
  roleSlug: string;
  level: string;
  skillId: string;
  skillName: string;
  specializationSlug?: string;
  topic: SkillTopicItemDto;
  availableTopics?: TopicDependencyOption[];
}

@Component({
  selector: 'app-edit-profile-topic-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSelectModule
  ],
  template: `
    <h2 mat-dialog-title>Edit topic on {{ data.skillName }}</h2>
    <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content class="content">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Topic</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Proficiency</mat-label>
          <mat-select formControlName="requiredProficiency">
            <mat-option value="beginner">beginner</mat-option>
            <mat-option value="intermediate">intermediate</mat-option>
            <mat-option value="advanced">advanced</mat-option>
            <mat-option value="expert">expert</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Importance</mat-label>
          <input matInput type="number" min="0" max="1" step="0.05" formControlName="importance" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Evidence mode</mat-label>
          <mat-select formControlName="evidenceMode">
            <mat-option *ngIf="!data.topic.evidenceMode" value="">Keep unclassified</mat-option>
            <mat-option value="implementation">Implementation — working artifacts or code</mat-option>
            <mat-option value="conceptual">Conceptual — explanation and reasoning</mat-option>
            <mat-option value="mixed">Mixed — implementation and explanation</mat-option>
          </mat-select>
          <mat-hint>Saving a selected mode confirms it for this topic and seniority.</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Depends on topics</mat-label>
          <mat-select formControlName="dependsOnKeys" multiple>
            <mat-option *ngFor="let opt of dependencyOptions" [value]="opt.label">{{ opt.label }}</mat-option>
          </mat-select>
        </mat-form-field>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || saving">Save</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .content { min-width: 420px; display: flex; flex-direction: column; gap: 0.35rem; }
      .full-width { width: 100%; }
      .error { color: #b00020; }
    `
  ]
})
export class EditProfileTopicDialogComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<EditProfileTopicDialogComponent, CareerRoleProfileSkillMutationResponse | undefined>);
  readonly data = inject<EditProfileTopicDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  saving = false;
  error = '';

  readonly dependencyOptions = (this.data.availableTopics ?? []).filter(
    (o) => !(o.skillId === this.data.skillId && o.topicName === this.data.topic.name)
  );

  readonly form = this.fb.nonNullable.group({
    name: [this.data.topic.name, [Validators.required, Validators.minLength(1)]],
    evidenceMode: [this.data.topic.evidenceMode ?? ''],
    requiredProficiency: [this.data.topic.level || 'intermediate', Validators.required],
    importance: [this.data.topic.importance ?? 0.5, [Validators.required, Validators.min(0), Validators.max(1)]],
    dependsOnKeys: this.fb.nonNullable.control<string[]>(
      (this.data.topic.dependsOn ?? []).map((d) => {
        const skillId = d.skillId || this.data.skillId;
        const match = this.dependencyOptions.find(
          (o) => o.skillId === skillId && o.topicName === d.topicName
        );
        return match?.label ?? `${d.topicName}`;
      })
    )
  });

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    const keys = this.form.value.dependsOnKeys ?? [];
    const request = {
      currentName: this.data.topic.name,
      evidenceMode: (this.form.value.evidenceMode || null) as import('../../../../models').TopicEvidenceMode | null,
      name: this.form.value.name?.trim(),
      requiredProficiency: this.form.value.requiredProficiency as string,
      importance: Number(this.form.value.importance ?? 0.5),
      dependsOn: keys
        .map((key) => this.dependencyOptions.find((o) => o.label === key))
        .filter((o): o is TopicDependencyOption => !!o)
        .map((o) => ({
          skillId: o.skillId === this.data.skillId ? null : o.skillId,
          topicName: o.topicName,
          kind: 'hard'
        }))
    };
    try {
      const result = this.data.specializationSlug
        ? await firstValueFrom(
            this.api.updateCareerRoleFocusSkillTopic(
              this.data.roleSlug,
              this.data.specializationSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          )
        : await firstValueFrom(
            this.api.updateCareerRoleCoreSkillTopic(
              this.data.roleSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          );
      this.dialogRef.close(result);
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.saving = false;
    }
  }
}
